import { ArrowRight, ArrowUpRight, PenLine } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { isBlogLocale, type BlogFeed, type BlogLocale, type BlogPost } from '@/config/blog';
import { loadBlogFeed } from '@/lib/blog-feed';
import { useNearViewport } from '@/lib/use-near-viewport';

type FeedState = { readonly status: 'idle' | 'loading' | 'hidden' } | { readonly status: 'ready'; readonly feed: BlogFeed };

/**
 * Latest articles from the blog, at the foot of the home page. Loads only as
 * it nears the viewport, from the site's own cached endpoint, and disappears
 * entirely when there is nothing to show or the blog can't be reached.
 */
export function BlogSection() {
  const { i18n } = useTranslation();
  const locale: BlogLocale = isBlogLocale(i18n.language) ? i18n.language : 'en';
  const [anchor, near] = useNearViewport<HTMLDivElement>();
  const state = useBlogFeed(locale, near);

  return (
    <div ref={anchor}>
      {state.status === 'loading' && <LoadingSection />}
      {state.status === 'ready' && <ArticlesSection feed={state.feed} locale={locale} />}
    </div>
  );
}

function useBlogFeed(locale: BlogLocale, enabled: boolean): FeedState {
  const [state, setState] = useState<FeedState>({ status: 'idle' });
  useEffect(() => {
    if (!enabled) return;
    let current = true;
    // First load shows the placeholder; a language switch keeps the current list until the new one is in.
    setState((previous) => (previous.status === 'idle' ? { status: 'loading' } : previous));
    void loadBlogFeed(locale).then((feed) => {
      if (current) setState(feed && feed.posts.length > 0 ? { status: 'ready', feed } : { status: 'hidden' });
    });
    return () => {
      current = false;
    };
  }, [locale, enabled]);
  return state;
}

function Shell({ lead, action, busy = false, children }: { readonly lead: string; readonly action?: ReactNode; readonly busy?: boolean; readonly children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <section
      className="mt-20 grid gap-8 border-t border-line-strong pt-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)] lg:gap-16"
      aria-labelledby="blog-title"
      aria-busy={busy || undefined}
    >
      {/* On wide screens the heading and its link stay in view beside the list. */}
      <div className="flex flex-col items-start gap-3 lg:sticky lg:top-8 lg:self-start">
        <h2 id="blog-title" className="text-[28px] leading-tight font-semibold tracking-[-0.03em] text-fg">
          {t('home.blog.title')}
        </h2>
        <p className="max-w-80 text-[15px] leading-relaxed text-pretty text-fg-muted">{lead}</p>
        {action}
      </div>
      {children}
    </section>
  );
}

function ArticlesSection({ feed, locale }: { readonly feed: BlogFeed; readonly locale: BlogLocale }) {
  const { t } = useTranslation();
  const language = feed.fallback ? fallbackLanguages(feed.posts, locale) : null;
  const lead = language ? t('home.blog.fallback', { language }) : t('home.blog.lead');
  return (
    <Shell
      lead={lead}
      action={
        feed.blogUrl && (
          <a
            href={feed.blogUrl}
            className="group -mx-1 mt-1 inline-flex items-center gap-1.5 rounded-lg px-1 py-0.5 text-sm font-medium text-fg outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            {t('home.blog.all')}
            <ArrowRight size={15} aria-hidden className="transition-transform duration-200 ease-out-soft group-hover:translate-x-0.5" />
          </a>
        )
      }
    >
      <ul role="list" className="-mt-5 divide-y divide-line-strong">
        {feed.posts.map((post) => (
          <li key={post.slug}>
            <Entry post={post} locale={locale} />
          </li>
        ))}
      </ul>
    </Shell>
  );
}

/** The languages a fallback list is in, named in the page language ("English", "English or German"), or null. */
export function fallbackLanguages(posts: readonly BlogPost[], locale: BlogLocale): string | null {
  const codes = [...new Set(posts.map((post) => post.locale).filter(Boolean))];
  if (codes.length === 0) return null;
  try {
    const names = new Intl.DisplayNames([locale], { type: 'language' });
    return new Intl.ListFormat(locale, { type: 'disjunction' }).format(codes.map((code) => names.of(code) ?? code));
  } catch {
    return null;
  }
}

/** One article as a ruled row; the whole row is the link, named by the title alone. */
function Entry({ post, locale }: { readonly post: BlogPost; readonly locale: BlogLocale }) {
  const date = useFormattedDate(post.publishedAt, locale);
  const foreign = post.locale && post.locale !== locale ? post.locale : undefined;
  return (
    <article
      lang={foreign}
      className="group relative -mx-3 grid grid-cols-[minmax(0,1fr)_88px] items-start gap-4 rounded-2xl px-3 py-5 transition-colors duration-200 ease-out-soft hover:bg-panel/70 has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-accent sm:grid-cols-[minmax(0,1fr)_200px] sm:gap-8"
    >
      <div className="min-w-0">
        {(date || post.topic) && (
          <p className="flex flex-wrap items-center gap-x-2 text-[13px] text-fg-subtle">
            {date && (
              <time dateTime={post.publishedAt} className="tabular">
                {date}
              </time>
            )}
            {date && post.topic && <span aria-hidden>·</span>}
            {post.topic && <span>{post.topic}</span>}
          </p>
        )}
        <h3 className="mt-1.5 text-[17px] leading-snug font-semibold tracking-[-0.02em] text-pretty text-fg sm:text-lg">
          {/* Stretched over the row; the row draws the focus ring. */}
          <a href={post.url} className="outline-none after:absolute after:inset-0 after:rounded-2xl after:content-['']">
            {post.title}
          </a>
          <ArrowUpRight
            size={16}
            aria-hidden
            className="ml-1 inline-block -translate-x-1 align-[-2px] text-fg-subtle opacity-0 transition-[opacity,transform] duration-200 ease-out-soft group-hover:translate-x-0 group-hover:opacity-100 motion-reduce:transition-none"
          />
        </h3>
        {post.excerpt && <p className="mt-2 line-clamp-2 text-[15px] leading-relaxed text-fg-muted max-sm:hidden">{post.excerpt}</p>}
        {post.author && (
          <p className="mt-3 flex items-center gap-2 text-[13px] text-fg-muted">
            <Avatar name={post.author.name} src={post.author.avatar} />
            {post.author.name}
          </p>
        )}
      </div>
      <Cover post={post} />
    </article>
  );
}

function Cover({ post }: { readonly post: BlogPost }) {
  const [failed, setFailed] = useState(false);
  const cover = failed ? null : post.cover;
  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-xl bg-elevated ring-1 ring-line sm:aspect-[1.91/1]">
      {cover ? (
        <img
          src={cover.src}
          alt={cover.alt}
          width={cover.width ?? undefined}
          height={cover.height ?? undefined}
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
          className="size-full object-cover transition-transform duration-500 ease-out-soft group-hover:scale-[1.03] motion-reduce:transition-none"
        />
      ) : (
        <span className="grid size-full place-items-center text-fg-subtle" aria-hidden>
          <PenLine size={20} />
        </span>
      )}
    </div>
  );
}

function Avatar({ name, src }: { readonly name: string; readonly src: string | null }) {
  const [failed, setFailed] = useState(false);
  if (src && !failed) {
    return <img src={src} alt="" width={20} height={20} loading="lazy" decoding="async" onError={() => setFailed(true)} className="size-5 shrink-0 rounded-full object-cover ring-1 ring-line" />;
  }
  return (
    <span aria-hidden className="grid size-5 shrink-0 place-items-center rounded-full bg-raised text-[11px] font-semibold text-fg-muted">
      {name.trim().charAt(0).toLocaleUpperCase()}
    </span>
  );
}

function useFormattedDate(iso: string, locale: BlogLocale): string {
  return useMemo(() => {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? '' : new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
  }, [iso, locale]);
}

/** Holds the section's place while the first answer is on its way. */
function LoadingSection() {
  const { t } = useTranslation();
  return (
    <Shell lead={t('home.blog.lead')} busy>
      <div>
        <p className="sr-only" role="status">
          {t('home.blog.loading')}
        </p>
        <ul aria-hidden className="-mt-5 divide-y divide-line-strong">
          {[0, 1, 2].map((row) => (
            <li key={row} className="grid grid-cols-[minmax(0,1fr)_88px] gap-4 py-5 sm:grid-cols-[minmax(0,1fr)_200px] sm:gap-8">
              <div className="space-y-2.5 pt-0.5">
                <div className="h-3 w-24 animate-pulse rounded bg-elevated motion-reduce:animate-none" />
                <div className="h-4.5 w-4/5 animate-pulse rounded bg-elevated motion-reduce:animate-none" />
                <div className="h-3.5 w-full animate-pulse rounded bg-elevated motion-reduce:animate-none max-sm:hidden" />
                <div className="h-3.5 w-2/5 animate-pulse rounded bg-elevated motion-reduce:animate-none" />
              </div>
              <div className="aspect-[4/3] animate-pulse rounded-xl bg-elevated motion-reduce:animate-none sm:aspect-[1.91/1]" />
            </li>
          ))}
        </ul>
      </div>
    </Shell>
  );
}
